/**
 * Utility functions that accept something that is either an element or its id
 * and return the element.
 */
import { isString } from 'lodash';
import type { IMetaModel } from '../../../stores/model/metamodel';
import type { IObjectType } from '../../../stores/model/metamodel/object_type';
import type { IEventType } from '../../../stores/model/metamodel/event_type';
import type { IMethod } from '../../../stores/model/metamodel/method';
import type { IDependency } from '../../../stores/model/metamodel/dependency';
import type { IInheritance } from '../../../stores/model/metamodel/inheritance';
import type { IAttribute } from '../../../stores/model/metamodel/attribute';
import type { IFSM } from '../../../stores/model/metamodel/fsm';
import type { IState } from '../../../stores/model/metamodel/state';
import type { ITransition } from '../../../stores/model/metamodel/transition';
import type { IMultiplePropagationConstraint } from '../../../stores/model/metamodel/multiple_propagation_constraint';
import type { AttributeOwnerLike, OrId } from '../types';

export function ensureObject(
  metamodel: IMetaModel,
  objectOrId: OrId<IObjectType>
): IObjectType | undefined {
  if (isString(objectOrId)) {
    return metamodel.getObjectTypeById(objectOrId);
  } else {
    return objectOrId;
  }
}

export function ensureDependency(
  metamodel: IMetaModel,
  depOrId: OrId<IDependency>
): IDependency | undefined {
  if (isString(depOrId)) {
    return metamodel.getDependencyById(depOrId);
  } else {
    return depOrId;
  }
}

export function ensureInheritance(
  metamodel: IMetaModel,
  inhOrId: OrId<IInheritance>
): IInheritance | undefined {
  if (isString(inhOrId)) {
    return metamodel.getInheritanceById(inhOrId);
  } else {
    return inhOrId;
  }
}

export function ensureEvent(
  metamodel: IMetaModel,
  eventOrId: OrId<IEventType>
): IEventType | undefined {
  if (isString(eventOrId)) {
    return metamodel.getEventTypeById(eventOrId);
  } else {
    return eventOrId;
  }
}

export function ensureMethod(
  metamodel: IMetaModel,
  methodOrId: OrId<IMethod>
): IMethod | undefined {
  if (isString(methodOrId)) {
    return metamodel.getMethodById(methodOrId);
  } else {
    return methodOrId;
  }
}

export function ensureAttribute(
  owner: AttributeOwnerLike,
  attributeOrId: OrId<IAttribute>
): IAttribute | undefined {
  if (isString(attributeOrId)) {
    return owner.getAttributeById(attributeOrId);
  } else {
    return attributeOrId;
  }
}

export function ensureMPC(
  object: IObjectType,
  mpcOrId: OrId<IMultiplePropagationConstraint>
): IMultiplePropagationConstraint | undefined {
  if (isString(mpcOrId)) {
    return object.getMultiplePropagationConstraintById(mpcOrId);
  } else {
    return mpcOrId;
  }
}

export function ensureFSM(object: IObjectType, fsmOrId: OrId<IFSM>): IFSM | undefined {
  if (isString(fsmOrId)) {
    return object.getFSMById(fsmOrId);
  } else {
    return fsmOrId;
  }
}

export function ensureTransition(
  fsm: IFSM,
  transitionOrId: OrId<ITransition>
): ITransition | undefined {
  if (isString(transitionOrId)) {
    return fsm.getTransitionById(transitionOrId);
  } else {
    return transitionOrId;
  }
}

export function ensureState(fsm: IFSM, stateOrdId: OrId<IState>): IState | undefined {
  if (isString(stateOrdId)) {
    return fsm.getStateById(stateOrdId);
  } else {
    return stateOrdId;
  }
}

// The reverse: accepts something that either a model or its id and returns the
// id.
export function ensureId(modelOrId: OrId<{ id: string }>): string {
  if (isString(modelOrId)) {
    return modelOrId;
  } else {
    return modelOrId.id;
  }
}

export function uniqueName(list: Array<string | { name: string }>, baseName: string): string {
  const lcNames = list.map((e) => (isString(e) ? e.toLowerCase() : e.name.toLowerCase()));
  const lcBaseName = baseName.toLowerCase();
  let suffix = 1;
  while (lcNames.includes(`${lcBaseName}${suffix === 1 ? '' : suffix}`)) {
    suffix += 1;
  }
  return `${baseName}${suffix === 1 ? '' : suffix}`;
}

export function isObjectType(metamodel: IMetaModel, modelOrId: OrId<{ id: string }>): boolean {
  const id = ensureId(modelOrId);
  return metamodel.getObjectTypeById(id) != null;
}

export function isInheritance(metamodel: IMetaModel, modelOrId: OrId<{ id: string }>): boolean {
  const id = ensureId(modelOrId);
  return metamodel.getInheritanceById(id) != null;
}
